/**
 * CreatorAuth - Standalone auth page for Creator Portal
 * 
 * Invite-only sign up flow:
 * - Creators must click their invite link to set a password
 * - No public sign up allowed
 * - Sign In tab for returning creators
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, BarChart3, ArrowLeft, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

type AuthView = 'signin' | 'forgot-password' | 'set-password';

const CreatorAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [view, setView] = useState<AuthView>('signin');
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  
  // Use a ref to track password setup mode synchronously (avoids race conditions)
  const isSettingUpPasswordRef = useRef(false);
  const [isSettingUpFromInvite, setIsSettingUpFromInvite] = useState(false);

  // Password strength validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // Check for invite/recovery tokens in URL (from invite link)
  useEffect(() => {
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');

    // Handle invite or recovery flow from email link
    if ((type === 'recovery' || type === 'invite') && accessToken && refreshToken) {
      console.log('Invite/recovery token detected, setting session for password setup');
      
      // Set ref immediately (synchronous) to prevent race condition
      isSettingUpPasswordRef.current = true;
      setIsSettingUpFromInvite(true);
      setView('set-password');
      
      // Set the session from the tokens
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      }).then(async ({ data, error }) => {
        if (error) {
          console.error('Error setting session from invite:', error);
          toast({
            variant: "destructive",
            title: "Link expired or invalid",
            description: "This invitation link has expired. Please contact support for a new invite.",
          });
          isSettingUpPasswordRef.current = false;
          setIsSettingUpFromInvite(false);
          setView('signin');
        } else if (data.user?.email) {
          // Pre-fill the email from the authenticated session.
          // R125-L-1: don't log user email to console — same rule as
          // R31-L-6 (no PII in shop-operator devtools).
          setInviteEmail(data.user.email);
          console.log('[CreatorAuth] Session set for invite flow (user id:', data.user.id, ')');
        }
      });
    }
  }, [location.hash, toast]);

  // Check if already logged in (but not in password setup mode)
  useEffect(() => {
    // Check URL hash directly for invite/recovery tokens
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const type = hashParams.get('type');
    const hasInviteTokens = (type === 'recovery' || type === 'invite') && hashParams.get('access_token');
    
    // Don't set up auth listener if we're processing an invite
    if (hasInviteTokens || isSettingUpPasswordRef.current) {
      console.log('Skipping auth redirect check - password setup in progress');
      return;
    }

    const checkAuth = async () => {
      // Double-check ref before redirecting
      if (isSettingUpPasswordRef.current) return;
      
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user && !isSettingUpPasswordRef.current) {
        navigate("/creator", { replace: true });
      }
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, 'isSettingUp:', isSettingUpPasswordRef.current);
        
        // NEVER redirect if we're setting up password (check ref, not state)
        if (isSettingUpPasswordRef.current) {
          console.log('Blocking redirect - password setup in progress');
          return;
        }
        
        // Only redirect for normal sign-in events
        if (session?.user && event === 'SIGNED_IN') {
          navigate("/creator", { replace: true });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, location.hash]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            variant: "destructive",
            title: "Sign in failed",
            description: "Invalid email or password. If you haven't set your password yet, please use the link in your invitation email.",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Sign in failed",
            description: error.message,
          });
        }
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/creator/auth`,
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message,
        });
      } else {
        toast({
          title: "Check your email",
          description: "If you have an account, we've sent you a password reset link.",
        });
        setView('signin');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPasswordStrong) {
      toast({
        variant: "destructive",
        title: "Password requirements not met",
        description: "Please ensure your password meets all the requirements listed below.",
      });
      return;
    }

    if (!passwordsMatch) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Error setting password",
          description: error.message,
        });
      } else {
        toast({
          title: "Password set successfully!",
          description: "Welcome to the Creator Impact Portal.",
        });
        
        // Clear the setup flags
        isSettingUpPasswordRef.current = false;
        setIsSettingUpFromInvite(false);
        
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        
        // Navigate to portal
        navigate("/creator", { replace: true });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Password requirement indicator
  const PasswordRequirement = ({ met, label }: { met: boolean; label: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <CheckCircle2 className="h-4 w-4 text-green-600" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground" />
      )}
      <span className={met ? "text-green-600" : "text-muted-foreground"}>{label}</span>
    </div>
  );

  // Set Password View (shown after clicking invite link)
  if (view === 'set-password') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Create Your Password</CardTitle>
            <CardDescription>
              Set a secure password to access the Creator Impact Portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetPassword} className="space-y-4">
              {/* Email (read-only, pre-filled from invite) */}
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteEmail || ""}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  This is the email address your invitation was sent to.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">Password</Label>
                <PasswordInput
                  id="new-password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <PasswordInput
                  id="confirm-password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>

              {/* Password requirements */}
              <div className="p-3 bg-muted rounded-lg space-y-1">
                <p className="text-sm font-medium mb-2">Password requirements:</p>
                <PasswordRequirement met={passwordChecks.length} label="At least 8 characters" />
                <PasswordRequirement met={passwordChecks.uppercase} label="One uppercase letter" />
                <PasswordRequirement met={passwordChecks.lowercase} label="One lowercase letter" />
                <PasswordRequirement met={passwordChecks.number} label="One number" />
                {confirmPassword.length > 0 && (
                  <PasswordRequirement met={passwordsMatch} label="Passwords match" />
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || !isPasswordStrong || !passwordsMatch}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Account & Continue
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot Password View
  if (view === 'forgot-password') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <CardDescription>
              Enter your email and we'll send you a link to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Reset Link
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setView('signin')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Sign In View (no Sign Up tab - invite only)
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <BarChart3 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Creator Impact Portal</CardTitle>
          <CardDescription>
            View how your training content is performing across the OptiBay network
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signin-email">Email</Label>
              <Input
                id="signin-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="signin-password">Password</Label>
                <button
                  type="button"
                  onClick={() => setView('forgot-password')}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <PasswordInput
                id="signin-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </form>
          
          {/* Invite-only notice */}
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Invite Only</p>
                <p className="text-xs text-muted-foreground">
                  Access to the Creator Portal is by invitation only. If you've received an invitation email, 
                  click the link in that email to set up your account.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>
              Looking for the main app?{" "}
              <a href="/auth" className="text-primary hover:underline">
                Sign in here
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatorAuth;
